import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { AxiosError, CanceledError } from 'axios';
import { ExecutionResult, IOValuesType, useClient } from '../client';
import { AppContext } from '../context';
import { IONodeType } from './nodes/IONode';

const requiredRules = [{ required: true, message: 'Required input!' }];

type TestPanelProps = {
  graphId: string;
  onClose: () => void;
  onExecutionResult: (result: ExecutionResult | null) => void;
  edgeData: IOValuesType[] | null;
  inputNodes: IONodeType[];
};

export default function TestPanel({
  graphId,
  onClose,
  edgeData,
  onExecutionResult,
  inputNodes,
}: TestPanelProps) {
  const [executing, setExecuting] = useState(false);
  const [inputData, setInputData] = useState<Record<string, any>>({});
  const [result, setResult] = useState('');
  const [executionResult, setExecutionResult] =
    useState<ExecutionResult | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const { projectId } = useContext(AppContext);
  const client = useClient(projectId);

  useEffect(() => {
    if (!executing || projectId === null) return;
    (async () => {
      try {
        const resultObj = await client.executeGraph(graphId, inputData, true);
        if (resultObj.error) {
          setResult(resultObj.error);
          messageApi.error('Error executing graph');
        } else {
          setResult(resultObj.output.join('\n\n================\n\n'));
        }
        setExecutionResult(resultObj);
        onExecutionResult(resultObj);
      } catch (error) {
        if (error instanceof CanceledError) return;
        messageApi.error('Error executing graph');
        console.log('Error executing graph:', error);
        if (error instanceof AxiosError) {
          setResult(JSON.stringify(error.response?.data));
        }
        setExecutionResult(null);
        onExecutionResult(null);
      }
      setExecuting(false);
    })();
  }, [executing, graphId, messageApi, projectId, onExecutionResult, inputData, client]);

  const edgeTextData = useMemo(
    () => (edgeData ? edgeData.join('\n\n================\n\n') : ''),
    [edgeData]
  );

  const llmStats = executionResult?.llm_stats;
  const llmNodeStats = executionResult?.llm_node_stats;
  let completePrice = false;
  if (llmNodeStats) {
    completePrice = Object.values(llmNodeStats).every(
      (stats) => stats.approximate_cost > 0
    );
  }

  const inputs = inputNodes.map((node, idx) => {
    let input = null;
    let formItemProps = {};
    switch (node.data.dataType) {
      case 'text':
        input = <TextArea placeholder="Input text" />;
        break;
      case 'file':
      case 'image':
        input = <Input type="file" multiple />;
        formItemProps = {
          valuePropName: 'files',
          getValueFromEvent: (e: any) => e?.target?.files,
        };
        break;
    }
    if (!input) {
      return null;
    }
    return (
      <Form.Item
        key={idx}
        name={`input-${idx}`}
        rules={requiredRules}
        {...formItemProps}
      >
        {input}
      </Form.Item>
    );
  });

  return (
    <div className="test-panel">
      {contextHolder}
      <div className="test-panel-left-col">
        <Form
          id="test-inputs-form"
          onFinish={(newParams: Record<string, any>) => {
            console.log('newParams', newParams);
            setInputData(newParams);
            setExecuting(true);
          }}
          disabled={executing}
        >
          <div className="test-panel-buttons">
            <Button icon={<CaretDownOutlined />} onClick={onClose}>
              Hide
            </Button>
            <Button
              icon={<CaretRightOutlined />}
              // onClick={() => setExecuting(true)}
              loading={executing}
              type="primary"
              htmlType="submit"
            >
              Run
            </Button>
          </div>

          {inputs}
        </Form>
      </div>
      <div className="test-panel-middle-col">
        <TextArea value={result} placeholder="Result" readOnly />
        {llmStats && (
          <>
            <Typography.Text>
              Completion tokens: <b>{llmStats.completion_tokens}</b>
            </Typography.Text>
            <Typography.Text>
              Prompt tokens: <b>{llmStats.prompt_tokens}</b>
            </Typography.Text>
            <Typography.Text>
              Total tokens: <b>{llmStats.total_tokens}</b>
            </Typography.Text>
            {llmStats.approximate_cost > 0 && (
              <Typography.Text>
                Approximate cost:{' '}
                <b>
                  {completePrice ? '' : '> '}$
                  {llmStats.approximate_cost.toFixed(6)}
                </b>
              </Typography.Text>
            )}
          </>
        )}
      </div>
      <div className="test-panel-right-col">
        <TextArea value={edgeTextData} placeholder="Object info" readOnly />
      </div>
    </div>
  );
}
